import { useEffect, useState } from 'react';
import { CALENDAR_COLOR_PALETTE_KEY, CALENDAR_COLOR_PRESETS_KEY, CalendarColorPalette, DEFAULT_CALENDAR_COLOR_PALETTE, MODELS_CALENDAR_EVENT_COLORS } from 'dashboard409a/common/constants/calendar';
import { ENGAGEMENT_TYPE_COLOR_MAP } from 'dashboard409a/pages/Workboard/common/constants';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { Logger } from 'utilities/logger';

export type ColorPalettePreset = {
  name: string;
  palette: CalendarColorPalette;
};

// Utility functions for color palette management
export const getCalendarColorPalette = (): CalendarColorPalette => {
  try {
    const saved = globalThis.localStorage.getItem(CALENDAR_COLOR_PALETTE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<CalendarColorPalette>;
      return {
        ...DEFAULT_CALENDAR_COLOR_PALETTE,
        ...parsed,
      };
    }
  } catch {
    return DEFAULT_CALENDAR_COLOR_PALETTE;
  }

  return DEFAULT_CALENDAR_COLOR_PALETTE;
};

export const getEngagementTypeColor = (engagementType: string): string => ENGAGEMENT_TYPE_COLOR_MAP[engagementType as keyof typeof ENGAGEMENT_TYPE_COLOR_MAP] || '#000000';

export const saveCalendarColorPalette = (palette: Partial<CalendarColorPalette>): void => {
  if (globalThis.window === undefined) {
    return;
  }

  try {
    const current = getCalendarColorPalette();
    const merged = { ...current, ...palette };
    globalThis.localStorage.setItem(CALENDAR_COLOR_PALETTE_KEY, JSON.stringify(merged));
  } catch {
    Logger.error('Error saving calendar color palette', new Error('Error saving calendar color palette'));
  }
};

export const resetCalendarColorPalette = (): void => {
  if (globalThis.window === undefined) {
    return;
  }

  try {
    globalThis.localStorage.removeItem(CALENDAR_COLOR_PALETTE_KEY);
  } catch {
    Logger.error('Error resetting calendar color palette', new Error('Error resetting calendar color palette'));
  }
};

// Get status color with localStorage fallback
// Note: Status 5 (WAITING) is deprecated. The is_waiting boolean field on Fulfillment
// is now used instead of model_status=5 to indicate a paused/waiting state.
// The mapping for status 5 is kept here for backward compatibility with any
// existing calendar events or UI elements that may still reference it.
export const getStatusColor = (status: number): string => {
  const palette = getCalendarColorPalette();
  const statusMap: Record<number, keyof CalendarColorPalette> = {
    0: 'DRAFT',
    1: 'REVIEW',
    2: 'IN_PROCESS_SCALAR_USA',
    3: 'FINAL',
    4: 'COMPLETE',
    5: 'WAITING', // Deprecated: use is_waiting field instead
  };
  return palette[statusMap[status]] || MODELS_CALENDAR_EVENT_COLORS.DEFAULT;
};

// Helper to convert color with alpha to solid color for color input
export const toSolidColor = (color: string): string => {
  // If color has 8 characters (with alpha), strip the alpha
  if (color.length === 9 && color.startsWith('#')) {
    return color.slice(0, 7);
  }
  // If color is rgba format, convert to hex
  if (color.startsWith('rgba')) {
    const matches = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(color);
    if (matches) {
      const r = Number.parseInt(matches[1], 10).toString(16).padStart(2, '0');
      const g = Number.parseInt(matches[2], 10).toString(16).padStart(2, '0');
      const b = Number.parseInt(matches[3], 10).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }
  }
  return color;
};

// Preset management functions
export const getColorPalettePresets = (): ColorPalettePreset[] => {
  try {
    const saved = localStorage.getItem(CALENDAR_COLOR_PRESETS_KEY);
    if (saved) {
      return JSON.parse(saved) as ColorPalettePreset[];
    }
  } catch {
    return [];
  }

  return [];
};

export const saveColorPalettePreset = (name: string, palette: CalendarColorPalette): void => {
  try {
    const presets = getColorPalettePresets();
    const existingIndex = presets.findIndex(p => p.name === name);
    
    if (existingIndex >= 0) {
      presets[existingIndex] = { name, palette };
    } else {
      presets.push({ name, palette });
    }

    localStorage.setItem(CALENDAR_COLOR_PRESETS_KEY, JSON.stringify(presets));
  } catch {
    Logger.error('Error saving color palette preset', new Error('Error saving color palette preset'));
  }
};

export const deleteColorPalettePreset = (name: string): void => {

  try {
    const presets = getColorPalettePresets();
    const filtered = presets.filter(p => p.name !== name);
    localStorage.setItem(CALENDAR_COLOR_PRESETS_KEY, JSON.stringify(filtered));
  } catch {
    Logger.error('Error deleting color palette preset', new Error('Error deleting color palette preset'));
  }
};

export const applyColorPalettePreset = (name: string): boolean => {
  try {
    const presets = getColorPalettePresets();
    const preset = presets.find(p => p.name === name);
    
    if (preset) {
      saveCalendarColorPalette(preset.palette);
      return true;
    }
  } catch {
    Logger.error('Error applying color palette preset', new Error('Error applying color palette preset'));
  }

  return false;
};

/**
 * Custom hook for managing color palette state and operations
 * @returns Object containing colorPalette, presets, and handler functions
 */
export const useColorPalette = () => {
  const [colorPalette, setColorPalette] = useLocalStorage<CalendarColorPalette>(
    CALENDAR_COLOR_PALETTE_KEY,
    DEFAULT_CALENDAR_COLOR_PALETTE
  );
  const [presets, setPresets] = useState<ColorPalettePreset[]>(() => getColorPalettePresets());
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Load presets from localStorage on mount
  useEffect(() => {
    setPresets(getColorPalettePresets());
  }, []);

  const handleColorChange = (key: keyof CalendarColorPalette, color: string) => {
    setColorPalette(prev => ({ ...prev, [key]: color }));
  };

  const handleReset = () => {
    setColorPalette(DEFAULT_CALENDAR_COLOR_PALETTE);
  };

  const handleSavePreset = () => {
    if (presetName.trim()) {
      saveColorPalettePreset(presetName.trim(), colorPalette);
      setPresets(getColorPalettePresets());
      setPresetName('');
      setSavePresetOpen(false);
    }
  };

  const handleOpenSavePreset = () => {
    setSavePresetOpen(true);
  };

  const handleCloseSavePreset = () => {
    setSavePresetOpen(false);
    setPresetName('');
  };

  const handleApplyPreset = (name: string) => {
    const presetsList = getColorPalettePresets();
    const preset = presetsList.find(p => p.name === name);
    
    if (preset) {
      setColorPalette(preset.palette);
    }
  };

  const handleDeletePreset = (name: string) => {
    deleteColorPalettePreset(name);
    setPresets(getColorPalettePresets());
  };

  return {
    colorPalette,
    presets,
    savePresetOpen,
    presetName,
    setPresetName,
    handleColorChange,
    handleReset,
    handleSavePreset,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleApplyPreset,
    handleDeletePreset,
  };
};